import React from "react";
import { useQuery, gql } from "@apollo/client";
import Chart from "react-apexcharts";
import Loader from "../../Loader";

const GET_ANALYTICS_LOCATION_BREAKDOWN = gql`
  query getAnalyticsLocationBreakdown($startDate: String!, $endDate: String!, $limit: Int) {
    getAnalyticsLocationBreakdown(startDate: $startDate, endDate: $endDate, limit: $limit) {
      status
      message
      data {
        latitude
        longitude
        address
        captureCount
        uniqueUserCount
      }
    }
  }
`;

// Plain 2D scatter of real lat/lng — no map tiles/new mapping library, but
// clusters visually mirror where users actually are since we're plotting
// real coordinates. Bubble size = unique users at that ~1.1km grid cell.
const LocationBubbleChart = ({ rows }) => {
  const series = [
    {
      name: "App opens",
      data: rows.map((row) => ({
        x: row.longitude,
        y: row.latitude,
        z: row.uniqueUserCount,
      })),
    },
  ];

  return (
    <Chart
      options={{
        chart: { id: "analytics-location-breakdown", toolbar: { show: false } },
        colors: ["#FBB034"],
        xaxis: {
          title: { text: "Longitude", style: { color: "#FFFFFF" } },
          labels: { style: { colors: "#FFFFFF" } },
        },
        yaxis: {
          title: { text: "Latitude", style: { color: "#FFFFFF" } },
          labels: { style: { colors: "#FFFFFF" } },
        },
        tooltip: {
          // Closes over `rows` directly — dataPointIndex lines up with it
          // since `series[0].data` above was built by mapping over `rows`
          // in the same order.
          custom: function ({ dataPointIndex }) {
            const row = rows[dataPointIndex];
            if (!row) return "";
            return `<div style="padding:8px;">
              <strong>${row.address || "Unknown address"}</strong><br/>
              <span>Unique users: ${row.uniqueUserCount}</span><br/>
              <span>App opens: ${row.captureCount}</span>
            </div>`;
          },
        },
      }}
      series={series}
      type="bubble"
      width="100%"
      height={350}
    />
  );
};

const LocationBreakdownWidget = ({ filters }) => {
  const { data, loading, error } = useQuery(GET_ANALYTICS_LOCATION_BREAKDOWN, {
    variables: { startDate: filters.startDate, endDate: filters.endDate, limit: 100 },
    fetchPolicy: "network-only",
  });

  const rows = data?.getAnalyticsLocationBreakdown?.data || [];

  return (
    <div className="bg-[#1C1C1E] p-6 rounded-2xl shadow-lg border border-gray-800">
      <h3 className="text-[#FBB033] font-bold text-xl mb-4">Where App Opens Come From</h3>
      {loading && <Loader />}
      {error && <div className="text-red-500">Error loading location breakdown</div>}

      {!loading && rows.length === 0 && (
        <div className="text-gray-500">No location data captured in this range yet.</div>
      )}

      {rows.length > 0 && (
        <>
          <LocationBubbleChart rows={rows} />
          <div className="overflow-x-auto mt-6">
            <table className="w-full text-sm text-left text-gray-300">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="py-2 pr-4">Area</th>
                  <th className="py-2 pr-4">Lat, Lng</th>
                  <th className="py-2 pr-4">Unique Users</th>
                  <th className="py-2">App Opens</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index} className="border-b border-gray-800">
                    <td className="py-2 pr-4 break-all">{row.address || "Unknown"}</td>
                    <td className="py-2 pr-4 font-mono text-xs">
                      {row.latitude}, {row.longitude}
                    </td>
                    <td className="py-2 pr-4">{row.uniqueUserCount}</td>
                    <td className="py-2">{row.captureCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default LocationBreakdownWidget;
